import React, { useEffect, useState } from 'react';
import { Share2, X, Copy, Trash2, Check, Loader2, Plus, Calendar, ExternalLink, Eye, FileDown, ChevronDown, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DURATION_OPTIONS = [
  { value: 7, label: '7 jours' },
  { value: 30, label: '30 jours' },
  { value: 90, label: '90 jours' },
  { value: 365, label: '1 an' },
  { value: 0, label: 'Sans expiration' },
];

/**
 * Modal de gestion des liens de partage (lecture seule) d'un rapport.
 *
 * Props:
 *   open: bool
 *   onClose: () => void
 *   rapportType: 'ticfe' | 'ticgn' | 'turpe' | 'turpe_multi'
 *   rapportId: string
 *   rapportLabel?: string  (titre affiché dans le modal)
 */
export default function ShareModal({ open, onClose, rapportType, rapportId, rapportLabel }) {
  const [shares, setShares] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [duration, setDuration] = useState(30);
  const [copiedToken, setCopiedToken] = useState(null);
  const [expandedLog, setExpandedLog] = useState(null);

  useEffect(() => {
    if (open && rapportType && rapportId) loadShares();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, rapportType, rapportId]);

  const loadShares = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(
        `${API_URL}/api/share/list?rapport_type=${rapportType}&rapport_id=${rapportId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (r.ok) setShares(await r.json());
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    setCreating(true);
    try {
      const token = localStorage.getItem('token');
      const body = { rapport_type: rapportType, rapport_id: rapportId };
      if (duration > 0) body.duration_days = duration;
      else body.duration_days = null;

      const r = await fetch(`${API_URL}/api/share`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      if (!r.ok) throw new Error(await r.text());
      const newShare = await r.json();
      setShares([newShare, ...shares]);
      toast.success('Lien de partage créé');
      // Auto copy
      const url = buildShareUrl(newShare.token);
      navigator.clipboard.writeText(url).catch(() => {});
      setCopiedToken(newShare.token);
      setTimeout(() => setCopiedToken(null), 2000);
    } catch (e) {
      toast.error('Erreur lors de la création du lien');
      console.error(e);
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (shareId) => {
    if (!window.confirm('Révoquer ce lien ? Le destinataire n\'y aura plus accès.')) return;
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API_URL}/api/share/${shareId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error();
      toast.success('Lien révoqué');
      loadShares();
    } catch (e) {
      toast.error('Erreur lors de la révocation');
    }
  };

  const buildShareUrl = (token) => `${window.location.origin}/share/${token}`;

  const handleCopy = async (token) => {
    await navigator.clipboard.writeText(buildShareUrl(token));
    setCopiedToken(token);
    toast.success('Lien copié dans le presse-papier');
    setTimeout(() => setCopiedToken(null), 2000);
  };

  const formatExpiry = (s) => {
    if (s.revoked) return { label: 'Révoqué', color: 'text-red-500' };
    if (!s.expires_at) return { label: 'Sans expiration', color: 'text-emerald-600' };
    const exp = new Date(s.expires_at);
    const now = new Date();
    if (exp < now) return { label: 'Expiré', color: 'text-red-500' };
    const days = Math.ceil((exp - now) / (1000 * 60 * 60 * 24));
    return {
      label: `Expire le ${exp.toLocaleDateString('fr-FR')} (${days} j.)`,
      color: 'text-gray-600',
    };
  };

  const isActive = (s) => {
    if (s.revoked) return false;
    if (!s.expires_at) return true;
    return new Date(s.expires_at) > new Date();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4" data-testid="share-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
              <Share2 className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="font-semibold text-gray-900">Partager le rapport</h2>
              {rapportLabel && (
                <p className="text-xs text-gray-500 truncate max-w-md">{rapportLabel}</p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg"
            data-testid="share-modal-close"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          <p className="text-sm text-gray-600">
            Générez un lien public en lecture seule. Le destinataire pourra consulter le rapport,
            le télécharger en PDF et l'imprimer, mais ne pourra ni le modifier ni le supprimer.
          </p>

          {/* Création d'un nouveau lien */}
          <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
            <h3 className="text-sm font-medium text-gray-900 mb-3 flex items-center gap-2">
              <Plus className="w-4 h-4" /> Nouveau lien
            </h3>
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1">
                <label className="block text-xs text-gray-500 mb-1 flex items-center gap-1">
                  <Calendar className="w-3 h-3" /> Durée de validité
                </label>
                <select
                  value={duration}
                  onChange={(e) => setDuration(Number(e.target.value))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
                  data-testid="share-duration-select"
                >
                  {DURATION_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-end">
                <button
                  onClick={handleCreate}
                  disabled={creating}
                  className="bg-[#397DFF] hover:bg-[#2a68d6] text-white font-medium px-5 py-2 rounded-lg text-sm disabled:opacity-60 flex items-center gap-2"
                  data-testid="share-create-btn"
                >
                  {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}
                  Générer le lien
                </button>
              </div>
            </div>
          </div>

          {/* Liste des liens */}
          <div>
            <h3 className="text-sm font-medium text-gray-900 mb-3">
              Liens existants {shares.length > 0 && <span className="text-gray-400 font-normal">({shares.length})</span>}
            </h3>

            {loading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
              </div>
            ) : shares.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-8 italic">
                Aucun lien de partage pour ce rapport.
              </p>
            ) : (
              <ul className="space-y-2" data-testid="share-list">
                {shares.map((s) => {
                  const exp = formatExpiry(s);
                  const active = isActive(s);
                  const url = buildShareUrl(s.token);
                  return (
                    <li
                      key={s.id}
                      className={`border rounded-lg p-3 ${
                        active ? 'border-gray-200 bg-white' : 'border-gray-100 bg-gray-50 opacity-70'
                      }`}
                    >
                      <div className="flex items-center justify-between gap-3 mb-2">
                        <div className="flex items-center gap-2 text-xs flex-wrap">
                          <span className={`font-medium ${exp.color}`}>{exp.label}</span>
                          <span className="text-gray-300">•</span>
                          <span className="text-gray-500">
                            Créé le {new Date(s.created_at).toLocaleDateString('fr-FR')}
                          </span>
                          <span className="text-gray-300">•</span>
                          <span className="inline-flex items-center gap-1 text-gray-600 font-medium" data-testid={`share-views-${s.id}`}>
                            <Eye className="w-3.5 h-3.5 text-blue-500" />
                            {(s.access_count_view ?? s.access_count ?? 0)} vue{((s.access_count_view ?? s.access_count ?? 0) > 1) ? 's' : ''}
                          </span>
                          {(s.access_count_pdf ?? 0) > 0 && (
                            <span className="inline-flex items-center gap-1 text-gray-600 font-medium" data-testid={`share-pdfs-${s.id}`}>
                              <FileDown className="w-3.5 h-3.5 text-emerald-500" />
                              {s.access_count_pdf} PDF
                            </span>
                          )}
                          {s.last_accessed_at && (
                            <>
                              <span className="text-gray-300">•</span>
                              <span className="text-gray-500" title={new Date(s.last_accessed_at).toLocaleString('fr-FR')}>
                                Dernier accès : {new Date(s.last_accessed_at).toLocaleDateString('fr-FR')}
                              </span>
                            </>
                          )}
                          {(s.access_log || []).length > 0 && (
                            <button
                              type="button"
                              onClick={() => setExpandedLog(expandedLog === s.id ? null : s.id)}
                              className="ml-1 inline-flex items-center gap-0.5 text-blue-600 hover:text-blue-700 hover:underline"
                              data-testid={`share-toggle-log-${s.id}`}
                            >
                              {expandedLog === s.id ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                              Journal
                            </button>
                          )}
                        </div>
                        {active && (
                          <button
                            onClick={() => handleRevoke(s.id)}
                            className="p-1.5 text-red-500 hover:bg-red-50 rounded-md"
                            title="Révoquer ce lien"
                            data-testid={`share-revoke-${s.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>

                      {expandedLog === s.id && (s.access_log || []).length > 0 && (
                        <div className="mb-2 max-h-40 overflow-y-auto bg-gray-50 rounded border border-gray-100" data-testid={`share-log-${s.id}`}>
                          <table className="w-full text-xs">
                            <thead className="bg-gray-100 sticky top-0">
                              <tr className="text-left text-gray-500">
                                <th className="px-2 py-1">Date</th>
                                <th className="px-2 py-1">Type</th>
                                <th className="px-2 py-1">IP</th>
                                <th className="px-2 py-1">Navigateur</th>
                              </tr>
                            </thead>
                            <tbody>
                              {[...(s.access_log || [])].reverse().map((e, idx) => (
                                <tr key={idx} className="border-t border-gray-100">
                                  <td className="px-2 py-1 text-gray-700 whitespace-nowrap">
                                    {new Date(e.timestamp).toLocaleString('fr-FR')}
                                  </td>
                                  <td className="px-2 py-1">
                                    {e.kind === 'pdf' ? (
                                      <span className="inline-flex items-center gap-0.5 text-emerald-600">
                                        <FileDown className="w-3 h-3" /> PDF
                                      </span>
                                    ) : (
                                      <span className="inline-flex items-center gap-0.5 text-blue-600">
                                        <Eye className="w-3 h-3" /> Vue
                                      </span>
                                    )}
                                  </td>
                                  <td className="px-2 py-1 text-gray-500 font-mono">{e.ip || '—'}</td>
                                  <td className="px-2 py-1 text-gray-500 truncate max-w-[200px]" title={e.user_agent}>
                                    {(e.user_agent || '').split(')')[0].slice(0, 30) || '—'}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                      {active && (
                        <div className="flex items-center gap-2">
                          <code className="flex-1 text-xs bg-gray-100 rounded-md px-2 py-1.5 truncate font-mono">
                            {url}
                          </code>
                          <button
                            onClick={() => handleCopy(s.token)}
                            className="p-2 bg-gray-100 hover:bg-gray-200 rounded-md"
                            title="Copier"
                            data-testid={`share-copy-${s.id}`}
                          >
                            {copiedToken === s.token ? (
                              <Check className="w-4 h-4 text-emerald-600" />
                            ) : (
                              <Copy className="w-4 h-4 text-gray-600" />
                            )}
                          </button>
                          <a
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 bg-gray-100 hover:bg-gray-200 rounded-md"
                            title="Ouvrir"
                          >
                            <ExternalLink className="w-4 h-4 text-gray-600" />
                          </a>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-gray-200 bg-gray-50 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-200 rounded-lg"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
}
